import { useState, useCallback, createContext, useContext } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { useLang } from "@/contexts/LangContext";

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const { t } = useLang();
  const [state, setState] = useState({ open: false, title: "", description: "", onConfirm: null, variant: "destructive" });

  const confirm = useCallback(({ title, description, variant = "destructive", confirmText }) => {
    return new Promise((resolve) => {
      setState({ open: true, title, description, variant, confirmText, onConfirm: () => resolve(true), onCancel: () => resolve(false) });
    });
  }, []);

  const handleClose = (confirmed) => {
    if (confirmed) state.onConfirm?.();
    else state.onCancel?.();
    setState(s => ({ ...s, open: false }));
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={state.open} onOpenChange={(o) => { if (!o) handleClose(false); }}>
        <DialogContent className="sm:max-w-md rounded-2xl border-[var(--th-border)] bg-[var(--th-card)]" data-testid="confirm-dialog">
          <DialogHeader className="border-b-0 pb-1">
            <DialogTitle className="flex items-center gap-2 text-[var(--th-text)]">
              {state.variant === "destructive" && <AlertTriangle className="w-5 h-5 text-red-400" />}
              {state.title}
            </DialogTitle>
            {state.description && (
              <DialogDescription className="text-[var(--th-muted)]">
                {state.description}
              </DialogDescription>
            )}
          </DialogHeader>
          <DialogFooter className="border-t-0 pt-1">
            <Button
              variant="outline"
              onClick={() => handleClose(false)}
              className="rounded-xl border-[var(--th-border)] text-[var(--th-text)] hover:bg-[var(--th-surface)]"
              data-testid="confirm-cancel"
            >
              {t("cancel")}
            </Button>
            <Button
              onClick={() => handleClose(true)}
              className={`rounded-xl ${state.variant === "destructive" ? "bg-red-500 hover:bg-red-600 text-white" : "bg-[var(--th-accent)] hover:bg-[var(--th-accent-h)] text-white"}`}
              data-testid="confirm-action"
            >
              {state.confirmText || t("confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within ConfirmProvider");
  return ctx;
}
